import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CornerDownRight,
  EllipsisVertical,
  Percent,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { v4 } from "uuid";
import { DragEndEvent } from "@dnd-kit/core";
import t from "@/lang/lang";
import { cn } from "cn";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableCellTextarea,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  DragNDropWrapper,
  ETableCellDragNDropGrip,
} from "@/shared/components/tables/e-table-cell-drag-n-drop";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TextInputField } from "@/shared/components/field/input-field";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { DocumentBase } from "@/services/backend/documents/types/base";
import { ServiceObjectReference } from "@/services/backend/documents/types/service-object-references-base";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import {
  newPriceModifier,
  PriceModifier,
  PriceModifierKind,
  priceModifierAmount,
  PriceModifierSchema,
  withPriceModifierAmount,
  withPriceModifierKind,
  withPriceModifierSchema,
} from "../../pricemodifier/types/price-modifier";
import {
  leadingGrouping,
  Position,
  PositionPatch,
  PositionSubscriptionIntervalSelectItems,
  subscriptionIntervalFromSelectValue,
  subscriptionIntervalToSelectValue,
} from "../types/position";
import { isSumKind, PositionKind } from "../types/position-kind";
import { PositionUnitSelectItems } from "../types/position-unit";
import {
  groupingAvailability,
  newSubPositions,
  OptionAvailability,
  PositionBaseData,
  PositionOptions,
  positionOptionsByAvailability,
} from "../types/position-base-data";
import { PositionCategorySelectItems } from "../types/position-category";
import {
  AddPositionRequest,
  PatchPositionRequest,
  RemoveGroupingRequest,
  RemovePositionRequest,
  RenameGroupingRequest,
} from "../types/position-requests";
import { PositionDetailsDialog } from "./position-details-dialog";
import { TableColumn } from "./positions-component";
import {
  PriceUsed,
  PricesUsedOf,
  priceUsedFromSelectValue,
  priceUsedSelectItems,
  priceUsedToSelectValue,
} from "../types/price-used";

/**
 * PositionsTableConfig configures a {@link PositionsTable}. groupName and
 * areaName name the groups and areas, e.g. "Gebäude" and "Raum".
 */
export type PositionsTableConfig = {
  groupName: string;
  areaName: string;
  // addPositionOptions are offered at each place, by the grouping of the
  // position added there, see groupingAvailability.
  addPositionOptions: PositionOptions;
  // groupings are the groupings users may add. Without any, the
  // positions are not grouped.
  groupings: ("group" | "area")[];
  // subPositionKinds are the kinds of the sub-positions users may add.
  subPositionKinds: PositionKind[];
  // tableColumns are the columns shown. The service object column is
  // only shown while the document has more than one service object.
  tableColumns: TableColumn[];
  // editableNumbers lets users set the numbers of positions, for
  // documents which do not number them by rules.
  editableNumbers?: boolean;
  // pricesUsed are the prices a position may show on documents, a rule
  // of the service kind.
  pricesUsed: PricesUsedOf;
};

/**
 * PositionsTableProps are the props of a {@link PositionsTable}. The on*
 * functions are the operations of `positions.Base`
 * (backend/core/omni/domain/positions/base.go) on the positions of the
 * document. They return the Promise of the save; the table reports its
 * persistence state and its errors.
 */
export type PositionsTableProps = {
  document: DocumentBase;
  config: PositionsTableConfig;
  positions: Position[];
  onPositionAdd: (request: AddPositionRequest) => Promise<unknown>;
  // onPositionPatch also moves positions: the backend moves a position
  // with a patched order, or a patched group or area, see
  // `positions.PatchPosition`.
  onPositionPatch: (request: PatchPositionRequest) => Promise<unknown>;
  onPositionRemove: (request: RemovePositionRequest) => Promise<unknown>;
  onGroupingRename: (request: RenameGroupingRequest) => Promise<unknown>;
  onGroupingRemove: (request: RemoveGroupingRequest) => Promise<unknown>;
  // disabled makes the table read-only. It is always read-only for an
  // immutable document.
  disabled?: boolean;
  // fieldNamePrefix prefixes the field names of the positions, the keys
  // of their field errors, for positions nested in the document, e.g.
  // "scopeOfWork.<id>." for "scopeOfWork.<id>.positions.<id>.text".
  fieldNamePrefix?: string;
};

type PositionsTableContextType = Omit<
  PositionsTableProps,
  "config" | "disabled" | "fieldNamePrefix"
> &
  Omit<PositionsTableConfig, "tableColumns"> & {
    // selectedColumns are the columns chosen by the user, columns those
    // of them which are shown
    columns: TableColumn[];
    selectedColumns: TableColumn[];
    setColumns: (columns: TableColumn[]) => void;
    disabled: boolean;
    fieldNamePrefix: string;
  };

const rejectingOperation = () => Promise.reject(new Error("no provider"));

const PositionsTableContext = createContext<PositionsTableContextType>({
  // The default is never read: every consumer renders inside the provider.
  document: {} as DocumentBase,
  positions: [],
  groupName: "",
  areaName: "",
  addPositionOptions: positionOptionsByAvailability([]),
  groupings: [],
  subPositionKinds: [],
  pricesUsed: () => ({ allowed: [], byDefault: PriceUsed.Zero }),
  columns: [],
  selectedColumns: [],
  setColumns: () => null,
  onPositionAdd: rejectingOperation,
  onPositionPatch: rejectingOperation,
  onPositionRemove: rejectingOperation,
  onGroupingRename: rejectingOperation,
  onGroupingRemove: rejectingOperation,
  disabled: false,
  fieldNamePrefix: "",
});

const usePositionsTableContext = () => useContext(PositionsTableContext);

const tableColumnsList = Object.values(TableColumn);

function columnKeys(columns: TableColumn[]): string {
  return columns.map((c) => c.key).join(",");
}

/**
 * PositionsTable renders the positions as one table in their order, like
 * the positions table of the PDF: the titles of groups and areas are rows
 * heading the positions within them. Whichever of group and area is used
 * first leads, the other is added within it, see {@link leadingGrouping}.
 *
 * Positions, groups and areas are added from the header, positions and
 * the other grouping within a title from its row. A dragged position is
 * patched with the order of the position it is dropped onto; the backend
 * moves it there, together with its block, and into the grouping of that
 * place.
 */
export function PositionsTable({
  config,
  positions,
  document,
  onPositionAdd,
  onPositionPatch,
  onPositionRemove,
  onGroupingRename,
  onGroupingRemove,
  disabled = false,
  fieldNamePrefix = "",
}: PositionsTableProps) {
  const [columns, setColumns] = useState<TableColumn[]>(config.tableColumns);
  // The config may change its columns with the positions. Only that
  // change is applied, so the columns chosen by the user are kept.
  const [configColumns, setConfigColumns] = useState(config.tableColumns);
  if (columnKeys(configColumns) !== columnKeys(config.tableColumns)) {
    const added = config.tableColumns.filter((c) => !configColumns.includes(c));
    const removed = configColumns.filter(
      (c) => !config.tableColumns.includes(c),
    );
    setConfigColumns(config.tableColumns);
    setColumns(
      tableColumnsList.filter(
        (c) =>
          (columns.includes(c) || added.includes(c)) && !removed.includes(c),
      ),
    );
  }

  const contextValue = useMemo<PositionsTableContextType>(
    () => ({
      document,
      positions,
      groupName: config.groupName,
      areaName: config.areaName,
      addPositionOptions: config.addPositionOptions,
      groupings: config.groupings,
      subPositionKinds: config.subPositionKinds,
      editableNumbers: config.editableNumbers,
      pricesUsed: config.pricesUsed,
      // a position can only pick between several service objects
      columns: columns
        .filter(
          (column) =>
            column !== TableColumn.ServiceObject ||
            (document.serviceObjects?.length ?? 0) > 1,
        )
        .sort((a, b) => (a.columnOrder < b.columnOrder ? -1 : 1)),
      selectedColumns: columns,
      setColumns,
      onPositionAdd,
      onPositionPatch,
      onPositionRemove,
      onGroupingRename,
      onGroupingRemove,
      disabled: disabled || isImmutable(document),
      fieldNamePrefix,
    }),
    [
      document,
      positions,
      config,
      columns,
      onPositionAdd,
      onPositionPatch,
      onPositionRemove,
      onGroupingRename,
      onGroupingRemove,
      disabled,
      fieldNamePrefix,
    ],
  );

  return (
    <PositionsTableContext value={contextValue}>
      <div className="rounded-lg border shadow-sm">
        <GroupedPositions positions={positions} />
      </div>
    </PositionsTableContext>
  );
}

/**
 * useRunOperation returns a function running an operation of the
 * positions: it reports the persistence state, and the errors of a
 * failed save as a toast and in the ErrValidationFailedContext. It
 * resolves to whether the operation succeeded.
 */
function useRunOperation(): (operation: Promise<unknown>) => Promise<boolean> {
  const { write } = useErrValidationFailedContext();
  const { setPersistenceState } = usePersistenceContext();

  return (operation) => {
    setPersistenceState(PersistenceState.SAVING);
    return operation
      .then(() => true)
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e, false));
        write(e);
        return false;
      })
      .finally(() => {
        setPersistenceState(PersistenceState.SAVED);
      });
  };
}

/** Grouping is the group and area a title or a position belongs to. */
type Grouping = { group: string; area: string };

/**
 * useAddPosition returns a function adding a position of base to the
 * grouping. The total belongs to no group or area.
 */
function useAddPosition(): (
  base: PositionBaseData,
  grouping: Partial<Grouping>,
) => void {
  const { onPositionAdd } = usePositionsTableContext();
  const run = useRunOperation();

  return (base, grouping) => {
    void run(
      onPositionAdd({
        positionId: v4(),
        ...(base.kind !== PositionKind.Total && grouping),
        ...base,
        subPositions: newSubPositions(base),
      }),
    );
  };
}

/** usePatchPosition returns a function saving a patch of one position. */
function usePatchPosition(): (
  positionId: string,
  patch: PositionPatch,
) => void {
  const { onPositionPatch } = usePositionsTableContext();
  const run = useRunOperation();

  return (positionId, patch) => {
    void run(onPositionPatch({ positionId, ...patch }));
  };
}

function isTitleKind(kind: PositionKind): boolean {
  return kind === PositionKind.GroupTitle || kind === PositionKind.AreaTitle;
}

function titleField(kind: PositionKind): "group" | "area" {
  return kind === PositionKind.GroupTitle ? "group" : "area";
}

function GroupedPositions({ positions }: { positions: Position[] }) {
  const { columns, groupings, disabled } = usePositionsTableContext();
  const doPatch = usePatchPosition();

  const sorted = useMemo(
    () => [...positions].sort((a, b) => a.order - b.order),
    [positions],
  );
  const leader = leadingGrouping(positions);
  // the groupings added at the top: the leading one, or both while
  // neither is used
  const topFields = groupings.filter((field) => !leader || field === leader);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const target = positions.find((p) => p.id === over.id);
    // the total always stays last
    if (!target || target.kind === PositionKind.Total) {
      return;
    }
    doPatch(String(active.id), { order: target.order });
  };

  return (
    <DragNDropWrapper
      items={sorted.map((p) => p.id)}
      handleDragEnd={handleDragEnd}
      disabled={disabled}
    >
      {/* the wrapper draws the outer border, so the table omits its own */}
      <ETable className="border-none [&_th]:border-t-0 [&_tr>*:first-child]:border-l-0 [&_tr>*:last-child]:border-r-0">
        <ETableHeader>
          <ETableRow>
            {columns.map((column) =>
              column === TableColumn.DnDGrip ? (
                <ETableHead key={column.key} className="h-8 w-8 p-0">
                  <ColumnSettingsDropdown />
                </ETableHead>
              ) : column === TableColumn.Actions ? (
                <ETableHead key={column.key} className="h-8 w-8 p-0">
                  <GroupingActionsDropdown
                    grouping={{ group: "", area: "" }}
                    fields={topFields}
                  />
                </ETableHead>
              ) : (
                <ETableHead
                  key={column.key}
                  className={cn("h-8", column.className)}
                >
                  {column.title}
                </ETableHead>
              ),
            )}
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {sorted.length === 0 && <EmptyPositionsRow fields={topFields} />}
          {sorted.map((position) =>
            isTitleKind(position.kind) ? (
              <PositionRowTitle
                key={position.id}
                position={position}
                leading={titleField(position.kind) === leader}
                doPatch={(patch) => doPatch(position.id, patch)}
              />
            ) : (
              <PositionRow
                key={position.id}
                position={position}
                doPatch={(patch) => doPatch(position.id, patch)}
              />
            ),
          )}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

function PositionRow({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns } = usePositionsTableContext();

  switch (position.kind) {
    case PositionKind.AreaTitle:
    case PositionKind.GroupTitle:
      // rendered by PositionRowTitle
      return null;
    case PositionKind.Text:
      return <PositionRowText position={position} doPatch={doPatch} />;
    case PositionKind.SubTotal:
    case PositionKind.Total:
      return <PositionRowSum position={position} doPatch={doPatch} />;
    default:
      return (
        <ETableRow>
          {columns.map((column) => (
            <TableCell
              key={column.key}
              column={column}
              position={position}
              doPatch={doPatch}
            />
          ))}
        </ETableRow>
      );
  }
}

/**
 * PositionRowTitle is the row of a group or area title. The leading one
 * is written in upper case, like in the PDF.
 */
function PositionRowTitle({
  position,
  leading,
  doPatch,
}: {
  position: Position;
  leading: boolean;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled } = usePositionsTableContext();
  const field = titleField(position.kind);
  const grouping = { group: position.group, area: position.area };

  return (
    <ETableRow
      className={cn("font-semibold", leading ? "bg-muted" : "bg-muted/50")}
    >
      <ETableCellDragNDropGrip id={position.id} disabled={disabled} />
      <TableCell
        column={TableColumn.Number}
        position={position}
        doPatch={doPatch}
      />
      <ETableCell colSpan={columns.length - 3} className="p-0">
        <GroupingNameInput
          grouping={grouping}
          field={field}
          className={cn(
            "rounded-none border-none bg-transparent font-semibold shadow-none focus-visible:border-none focus-visible:ring-0 md:text-sm",
            leading && "uppercase",
          )}
        />
      </ETableCell>
      <ETableCell className="w-8 p-0">
        <GroupingActionsDropdown
          grouping={grouping}
          // the other grouping is only added within the leading one
          fields={leading ? [field === "group" ? "area" : "group"] : []}
          removable={field}
        />
      </ETableCell>
    </ETableRow>
  );
}

// sumRowColumns are the columns of a sum row rendering their own cell;
// the text spans the first run of the other columns
const sumRowColumns = new Set<TableColumn>([
  TableColumn.DnDGrip,
  TableColumn.Number,
  TableColumn.FinalPrice,
  TableColumn.SubscriptionInterval,
  TableColumn.SubscriptionPrice,
  TableColumn.Actions,
]);

function PositionRowSum({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled, fieldNamePrefix } = usePositionsTableContext();

  // group the other columns into runs between the columns with a cell
  // of their own
  const cells: (TableColumn | { run: number; start: string })[] = [];
  for (const column of columns) {
    const last = cells[cells.length - 1];
    if (sumRowColumns.has(column)) {
      cells.push(column);
    } else if (last !== undefined && "run" in last) {
      last.run++;
    } else {
      cells.push({ run: 1, start: column.key });
    }
  }
  const textStart = cells.find((c) => "run" in c);

  return (
    <ETableRow className="font-semibold">
      {cells.map((cell) => {
        if ("run" in cell) {
          return cell === textStart ? (
            <ETableCellInput
              key={cell.start}
              colSpan={cell.run}
              value={position.text}
              onChange={(e) => doPatch({ text: e.target.value })}
              fieldName={`${fieldNamePrefix}positions.${position.id}.text`}
              disabled={disabled}
            />
          ) : (
            <ETableCell
              key={cell.start}
              aria-hidden="true"
              colSpan={cell.run}
            />
          );
        }
        if (cell === TableColumn.DnDGrip) {
          return position.kind === PositionKind.Total ? (
            // the total stays last and cannot be dragged
            <ETableCell key={cell.key} aria-hidden="true" />
          ) : (
            <ETableCellDragNDropGrip
              key={cell.key}
              id={position.id}
              disabled={disabled}
            />
          );
        }
        return (
          <TableCell
            key={cell.key}
            column={cell}
            position={position}
            doPatch={doPatch}
          />
        );
      })}
    </ETableRow>
  );
}

// textRowColumns are the columns of a text row; the text spans the others
const textRowColumns = new Set<TableColumn>([
  TableColumn.DnDGrip,
  TableColumn.ServiceObject,
  TableColumn.Number,
  TableColumn.Text,
  TableColumn.Kind,
  TableColumn.Active,
  TableColumn.Area,
  TableColumn.Group,
  TableColumn.Actions,
]);

function PositionRowText({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled } = usePositionsTableContext();
  const columnsUsed = columns.filter((c) => textRowColumns.has(c));
  const colSpan = columns.length - columnsUsed.length + 1;

  return (
    <ETableRow>
      {columnsUsed.map((c) => {
        if (c === TableColumn.Text) {
          return (
            <ETableCellTextarea
              key={c.key}
              colSpan={colSpan}
              placeholder={t("Text eingeben ...")}
              value={position.text}
              onChange={(e) => doPatch({ text: e.target.value })}
              disabled={disabled}
            />
          );
        }
        return (
          <TableCell
            key={c.key}
            column={c}
            position={position}
            doPatch={doPatch}
          />
        );
      })}
    </ETableRow>
  );
}

function TableCell({
  column,
  position,
  doPatch,
  colSpan,
}: {
  column: TableColumn;
  position: Position;
  doPatch: (r: PositionPatch) => void;
  colSpan?: number;
}) {
  const { document, disabled, editableNumbers, pricesUsed, fieldNamePrefix } =
    usePositionsTableContext();

  switch (column.key) {
    case "dndGrip":
      return <ETableCellDragNDropGrip id={position.id} disabled={disabled} />;
    case "number":
      // Children numbered by the rules of the service kind show their
      // number, all others an indicator.
      if (position.parentId && !position.number) {
        return (
          <ETableCell colSpan={colSpan}>
            <CornerDownRight className="ml-2 size-5" />
          </ETableCell>
        );
      }
      return (
        <ETableCellInput
          key={column.key}
          className="w-20 font-semibold"
          value={position.number}
          onChange={(e) => doPatch({ number: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.number`}
          disabled={disabled}
          colSpan={colSpan}
          readOnly={!editableNumbers}
        />
      );
    case "serviceObjectId":
      return (
        <ETableCellSelect
          label={t("Serviceobjekt")}
          items={[
            { value: NullUUID, label: t("Kein Serviceobjekt") },
            ...(document.serviceObjects ?? []).map(
              (so: ServiceObjectReference) => ({
                value: so.serviceObjectId,
                label: <ServiceObjectName id={so.serviceObjectId} />,
              }),
            ),
          ]}
          value={position.serviceObjectId || NullUUID}
          onValueChange={(serviceObjectId) => doPatch({ serviceObjectId })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.serviceObjectId`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "productNumber":
      return (
        <ETableCellInput
          key={column.key}
          className="w-24"
          value={position.productNumber}
          onChange={(e) => doPatch({ productNumber: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.productNumber`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "textShort":
      return (
        <ETableCellInput
          key={column.key}
          className="w-48"
          value={position.textShort}
          onChange={(e) => doPatch({ textShort: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.textShort`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "text":
      return (
        <ETableCellInput
          key={column.key}
          className="w-full"
          value={position.text}
          onChange={(e) => doPatch({ text: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.text`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "kind":
    case "area":
    case "group":
      // group and area change by the title rows and by moving
      return (
        <ETableCell key={column.key} colSpan={colSpan}>
          {position[column.key]}
        </ETableCell>
      );
    case "active":
      return (
        <ETableCellCheckbox
          key={column.key}
          checked={position.active}
          onCheckedChange={(v) => doPatch({ active: v === true })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.active`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "category":
      return (
        <ETableCellSelect
          label={t("Kategorie")}
          items={PositionCategorySelectItems}
          value={position.category}
          onValueChange={(category) => doPatch({ category })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.category`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "cycle":
      // a price modifier is not repeated on its own
      if (
        position.kind === PositionKind.PriceModifierAbsolute ||
        position.kind === PositionKind.PriceModifierRelative
      ) {
        return <BlindCell />;
      }
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.cycle}
          onValueChange={(cycle) => doPatch({ cycle })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.cycle`}
          disabled={disabled}
          precision={0}
          colSpan={colSpan}
        />
      );
    case "hours":
      if (!hasHours(position.kind)) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.hours}
          onValueChange={(hours) => doPatch({ hours })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.hours`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "hoursRate":
      if (!hasHours(position.kind)) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.hoursRate}
          onValueChange={(hoursRate) => doPatch({ hoursRate })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.hoursRate`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "count":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.count}
          onValueChange={(count) => doPatch({ count })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.count`}
          disabled={disabled}
          precision={0}
          colSpan={colSpan}
        />
      );
    case "unit":
      return (
        <ETableCellSelect
          label={t("Einheit")}
          items={PositionUnitSelectItems}
          value={position.unit}
          onValueChange={(unit) => doPatch({ unit })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.unit`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "unitPrice":
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.unitPrice}
          onValueChange={(unitPrice) => doPatch({ unitPrice })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.unitPrice`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "basePrice":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.basePrice}
          onValueChange={() => null}
          precision={2}
          fieldName={`${fieldNamePrefix}positions.${position.id}.basePrice`}
          readOnly
          colSpan={colSpan}
        />
      );
    case "priceModifiers":
      return (
        <ETableCell className="p-0">
          <PriceModifierDialog
            position={position}
            onPriceModifierChange={(priceModifiers) =>
              doPatch({ priceModifiers })
            }
            trigger={<Button variant="ghost">{t("Rabatte")}</Button>}
          />
        </ETableCell>
      );
    case "finalPrice":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.finalPrice}
          onValueChange={() => null}
          precision={2}
          fieldName={`${fieldNamePrefix}positions.${position.id}.finalPrice`}
          readOnly
          colSpan={colSpan}
        />
      );
    case "subscriptionInterval":
      if (!hasSubscription(position.kind)) return <BlindCell />;
      return (
        <ETableCellSelect
          label={t("Abo-Intervall")}
          items={PositionSubscriptionIntervalSelectItems}
          value={subscriptionIntervalToSelectValue(
            position.subscriptionInterval,
          )}
          onValueChange={(value) =>
            doPatch({
              subscriptionInterval: subscriptionIntervalFromSelectValue(value),
            })
          }
          fieldName={`${fieldNamePrefix}positions.${position.id}.subscriptionInterval`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "subscriptionPrice":
      if (!hasSubscription(position.kind)) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.subscriptionPrice}
          onValueChange={() => null}
          precision={2}
          fieldName={`${fieldNamePrefix}positions.${position.id}.subscriptionPrice`}
          readOnly
          colSpan={colSpan}
        />
      );
    case "priceUsed": {
      const prices = pricesUsed(position.kind, position.serviceVariation);
      if (prices.allowed.length <= 1) return <BlindCell />;
      return (
        <ETableCellSelect
          label={t("Preis im Dokument")}
          items={priceUsedSelectItems(prices)}
          value={priceUsedToSelectValue(position.priceUsed)}
          onValueChange={(value) =>
            doPatch({ priceUsed: priceUsedFromSelectValue(value) })
          }
          fieldName={`${fieldNamePrefix}positions.${position.id}.priceUsed`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    }
    case "tableActions":
      return <PositionActions position={position} doPatch={doPatch} />;
  }
}

// A dynamic position has no final price, a text position no price at all,
// so neither has a subscription price.
function hasSubscription(kind: PositionKind): boolean {
  return kind !== PositionKind.Dynamic && kind !== PositionKind.Text;
}

function hasHours(kind: PositionKind): boolean {
  return (
    kind !== PositionKind.Dynamic &&
    kind !== PositionKind.PriceModifierAbsolute &&
    kind !== PositionKind.PriceModifierRelative
  );
}

function BlindCell() {
  return (
    <ETableCell className="bg-[repeating-linear-gradient(-45deg,var(--muted),var(--muted)_2px,transparent_2px,transparent_4px)]" />
  );
}

function PositionActions({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const {
    disabled,
    document,
    positions,
    groupings,
    editableNumbers,
    pricesUsed,
    fieldNamePrefix,
    onPositionPatch,
  } = usePositionsTableContext();
  const run = useRunOperation();

  return (
    <ETableCell className="w-8 p-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
            <PositionDetailsDialog
              position={position}
              parent={positions.find((p) => p.id === position.parentId)}
              document={document}
              groupings={groupings}
              editableNumbers={editableNumbers}
              pricesUsed={pricesUsed(position.kind, position.serviceVariation)}
              disabled={disabled}
              fieldNamePrefix={fieldNamePrefix}
              onConfirm={(patch) =>
                run(onPositionPatch({ positionId: position.id, ...patch }))
              }
            />
            <RemovePositionDialog position={position} />
            {/* sums are always top-level and carry no modifiers, as do
                positions of a modifier kind */}
            {!isSumKind(position.kind) && (
              <AddChildDropdownItems position={position} />
            )}
            {position.kind !== PositionKind.PriceModifierRelative &&
              position.kind !== PositionKind.PriceModifierAbsolute &&
              !isSumKind(position.kind) && (
                <PriceModifierDialog
                  position={position}
                  onPriceModifierChange={(priceModifiers) =>
                    doPatch({ priceModifiers })
                  }
                />
              )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}

function RemovePositionDialog({ position }: { position: Position }) {
  const { onPositionRemove } = usePositionsTableContext();
  const run = useRunOperation();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const doDelete = () => {
    if (isLoading) return;

    setIsLoading(true);
    void run(onPositionRemove({ positionId: position.id })).then((ok) => {
      setIsLoading(false);
      if (ok) setOpen(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setOpen(!open);
          }}
        >
          <Trash2 />
          {t("Löschen")}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Position löschen?")}</DialogTitle>
          <DialogDescription>
            {t("Die Position wird unwiederruflich gelöscht.")}
          </DialogDescription>
        </DialogHeader>
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

const subPositionKindLabels: Partial<Record<PositionKind, string>> = {
  [PositionKind.Text]: t("Text hinzufügen"),
  [PositionKind.Default]: t("Position hinzufügen"),
  [PositionKind.Dynamic]: t("Dynamische Position hinzufügen"),
};

/**
 * AddChildDropdownItems add a sub-position of each kind the service kind
 * allows, see {@link PositionsTableConfig}.
 */
function AddChildDropdownItems({ position }: { position: Position }) {
  const { onPositionAdd, subPositionKinds } = usePositionsTableContext();
  const run = useRunOperation();
  const [adding, setAdding] = useState<PositionKind>();

  // only one level nesting allowed
  if (position.parentId) return;

  const doAdd = (kind: PositionKind) => {
    if (adding !== undefined) return;

    setAdding(kind);
    void run(
      onPositionAdd({
        positionId: v4(),
        parentId: position.id,
        kind,
      }),
    ).then(() => setAdding(undefined));
  };

  return subPositionKinds.map((kind) => (
    <DropdownMenuItem key={kind} onSelect={() => doAdd(kind)}>
      {adding === kind ? <Spinner /> : <Plus />}
      {subPositionKindLabels[kind] ?? kind}
    </DropdownMenuItem>
  ));
}

const priceModifierKindSelectItems = [
  { value: PriceModifierKind.Discount, label: t("Rabatt") },
  { value: PriceModifierKind.Surcharge, label: t("Zuschlag") },
];

const priceModifierSchemaSelectItems = [
  { value: PriceModifierSchema.Relative, label: "%" },
  { value: PriceModifierSchema.Absolute, label: "€" },
];

function PriceModifierDialog({
  position,
  onPriceModifierChange,
  trigger,
}: {
  position: Position;
  onPriceModifierChange: (modifiers: PriceModifier[]) => void;
  trigger?: ReactNode;
}) {
  const { disabled, fieldNamePrefix } = usePositionsTableContext();
  const [open, setOpen] = useState(false);

  const modifiers = position.priceModifiers;
  const change = (i: number, modifier: PriceModifier) =>
    onPriceModifierChange(modifiers.map((m, j) => (j === i ? modifier : m)));
  const remove = (i: number) =>
    onPriceModifierChange(modifiers.filter((_, j) => j !== i));
  const add = () => onPriceModifierChange([...modifiers, newPriceModifier()]);
  const fieldName = (i: number, field: string) =>
    `${fieldNamePrefix}positions.${position.id}.priceModifiers.${i}.${field}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setOpen(!open);
            }}
          >
            <Percent />
            {t("Rabatte / Zuschläge")}
          </DropdownMenuItem>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("Rabatte für Position:")} {position.number}
          </DialogTitle>
        </DialogHeader>
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead>{t("Aktiv")}</ETableHead>
              <ETableHead className="w-full">{t("Bezeichnung")}</ETableHead>
              <ETableHead>{t("Art")}</ETableHead>
              <ETableHead>{t("Berechnung")}</ETableHead>
              <ETableHead>{t("Betrag")}</ETableHead>
              <ETableHead>{t("Wert")}</ETableHead>
              <ETableHead />
            </ETableRow>
          </ETableHeader>
          <ETableBody>
            {modifiers.length === 0 && <TableEmptyRow colSpan={7} />}
            {modifiers.map((modifier, i) => (
              // modifiers have no identity of their own, their index is
              // their key in the backend as well
              <ETableRow key={i}>
                <ETableCellCheckbox
                  checked={modifier.active}
                  onCheckedChange={(active) =>
                    change(i, { ...modifier, active: active === true })
                  }
                  fieldName={fieldName(i, "active")}
                  disabled={disabled}
                />
                <ETableCellInput
                  value={modifier.title}
                  onChange={(e) =>
                    change(i, { ...modifier, title: e.target.value })
                  }
                  fieldName={fieldName(i, "title")}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Art")}
                  items={priceModifierKindSelectItems}
                  value={modifier.kind}
                  onValueChange={(kind) =>
                    change(
                      i,
                      withPriceModifierKind(
                        modifier,
                        kind as PriceModifierKind,
                      ),
                    )
                  }
                  fieldName={fieldName(i, "kind")}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Berechnung")}
                  items={priceModifierSchemaSelectItems}
                  value={modifier.schema}
                  onValueChange={(schema) =>
                    change(
                      i,
                      withPriceModifierSchema(
                        modifier,
                        schema as PriceModifierSchema,
                      ),
                    )
                  }
                  fieldName={fieldName(i, "schema")}
                  disabled={disabled}
                />
                <ETableCellDecimalInput
                  value={priceModifierAmount(modifier)}
                  onValueChange={(amount) =>
                    change(i, withPriceModifierAmount(modifier, amount))
                  }
                  precision={2}
                  fieldName={fieldName(
                    i,
                    modifier.schema === PriceModifierSchema.Relative
                      ? "relativeRate"
                      : "value",
                  )}
                  disabled={disabled}
                />
                <ETableCellDecimalInput
                  value={modifier.value}
                  onValueChange={() => null}
                  precision={2}
                  readOnly
                />
                <ETableCell className="w-8 p-0">
                  <Button
                    variant="ghost"
                    className="w-8 rounded-none"
                    aria-label={t("Entfernen")}
                    title={t("Entfernen")}
                    onClick={() => remove(i)}
                    disabled={disabled}
                  >
                    <Trash2 />
                  </Button>
                </ETableCell>
              </ETableRow>
            ))}
          </ETableBody>
        </ETable>
        {!disabled && (
          <Button variant="outline" size="sm" className="w-fit" onClick={add}>
            <Plus />
            {t("Rabatt hinzufügen")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * GroupingNameInput renames the field of the grouping, e.g. the area of
 * a room, across all its positions. The rename is saved on blur or
 * Enter, not per keystroke: every save moves the positions to the new
 * name, and intermediate names may clash with existing groupings.
 */
function GroupingNameInput({
  grouping,
  field,
  className,
}: {
  grouping: Grouping;
  field: "group" | "area";
  className?: string;
}) {
  const { disabled, onGroupingRename } = usePositionsTableContext();
  const run = useRunOperation();
  const value = grouping[field];

  const [draft, setDraft] = useState(value);
  const [shownValue, setShownValue] = useState(value);
  if (shownValue !== value) {
    setShownValue(value);
    setDraft(value);
  }
  const skipCommit = useRef(false);

  const commit = () => {
    const name = draft.trim();
    if (skipCommit.current || name === "" || name === value) {
      skipCommit.current = false;
      setDraft(value);
      return;
    }
    const newKey = field === "group" ? "newGroup" : "newArea";
    void run(onGroupingRename({ ...grouping, [newKey]: name })).then((ok) => {
      if (!ok) setDraft(value);
    });
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          skipCommit.current = true;
          e.currentTarget.blur();
        }
      }}
      disabled={disabled || value === ""}
      className={className}
    />
  );
}

/**
 * EmptyPositionsRow is shown in a table without positions. It offers to
 * add the first position or grouping.
 */
function EmptyPositionsRow({ fields }: { fields: ("group" | "area")[] }) {
  const { columns, addPositionOptions, disabled } = usePositionsTableContext();
  const addPosition = useAddPosition();
  const groupName = useGroupingName("group");
  const areaName = useGroupingName("area");
  const [adding, setAdding] = useState<"group" | "area">();

  return (
    <ETableRow>
      <ETableCell colSpan={columns.length}>
        <div className="flex flex-wrap items-center justify-center gap-2 py-2">
          <span className="text-muted-foreground mr-2 text-sm">
            {t("Noch keine Positionen")}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={disabled}>
                <Plus />
                {t("Position hinzufügen")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              {addPositionOptions[OptionAvailability.ZeroGrouping].map(
                (option) => (
                  <DropdownMenuItem
                    key={option.label ?? option.text}
                    onSelect={() => addPosition(option, {})}
                  >
                    <Plus />
                    {option.label ?? option.text}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {fields.map((field) => (
            <Button
              key={field}
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => setAdding(field)}
            >
              <Plus />
              {`${field === "group" ? groupName : areaName} ${t("hinzufügen")}`}
            </Button>
          ))}
        </div>
        <AddGroupingDialog
          field={adding}
          grouping={{ group: "", area: "" }}
          onClose={() => setAdding(undefined)}
        />
      </ETableCell>
    </ETableRow>
  );
}

/**
 * GroupingActionsDropdown adds positions to the grouping, and the fields
 * as groupings within it. With removable, it removes the grouping, i.e.
 * all positions selected by its field.
 */
function GroupingActionsDropdown({
  grouping,
  fields,
  removable,
}: {
  grouping: Grouping;
  fields: ("group" | "area")[];
  removable?: "group" | "area";
}) {
  const { addPositionOptions, groupings, disabled } =
    usePositionsTableContext();
  const options = addPositionOptions[groupingAvailability(grouping)];
  const addable = fields.filter((field) => groupings.includes(field));
  const addPosition = useAddPosition();
  const groupName = useGroupingName("group");
  const areaName = useGroupingName("area");
  const [adding, setAdding] = useState<"group" | "area">();
  const [removing, setRemoving] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            {removable ? <EllipsisVertical /> : <Plus />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Position hinzufügen")}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.label ?? option.text}
                onSelect={() => addPosition(option, grouping)}
              >
                <Plus />
                {option.label ?? option.text}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {addable.length > 0 && (
            <>
              <DropdownMenuSeparator />
              {addable.map((field) => (
                <DropdownMenuItem key={field} onSelect={() => setAdding(field)}>
                  <Plus />
                  {`${field === "group" ? groupName : areaName} ${t("hinzufügen")}`}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {removable && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setRemoving(true)}>
                <Trash2 />
                {`${removable === "group" ? groupName : areaName} ${t("löschen")}`}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <AddGroupingDialog
        field={adding}
        grouping={grouping}
        onClose={() => setAdding(undefined)}
      />
      {removable && (
        <RemoveGroupingDialog
          open={removing}
          onOpenChange={setRemoving}
          grouping={grouping}
          field={removable}
        />
      )}
    </>
  );
}

/**
 * useGroupingName returns the configured name of the field, e.g.
 * "Gebäude" for the group.
 */
function useGroupingName(field: "group" | "area"): string {
  const { groupName, areaName } = usePositionsTableContext();
  return field === "group" ? groupName : areaName;
}

/**
 * AddGroupingDialog adds the title of a new grouping of field within the
 * grouping, e.g. a room within a building. It is open while field is set.
 */
function AddGroupingDialog({
  field,
  grouping,
  onClose,
}: {
  field?: "group" | "area";
  grouping: Grouping;
  onClose: () => void;
}) {
  const { onPositionAdd, fieldNamePrefix } = usePositionsTableContext();
  const run = useRunOperation();
  const [isLoading, setIsLoading] = useState(false);
  const [positionId, setPositionId] = useState(v4());
  // this is not the "text" of the title but the name of the grouping
  const [name, setName] = useState("");
  const label = useGroupingName(field ?? "group");

  const doAdd = () => {
    if (isLoading || !field) return;

    setIsLoading(true);
    void run(
      onPositionAdd({
        positionId,
        kind:
          field === "group" ? PositionKind.GroupTitle : PositionKind.AreaTitle,
        ...grouping,
        [field]: name,
      }),
    ).then((ok) => {
      setIsLoading(false);
      if (!ok) return;
      setName("");
      setPositionId(v4());
      onClose();
    });
  };

  return (
    <Dialog open={!!field} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${label} ${t("hinzufügen")}`}</DialogTitle>
        </DialogHeader>
        <TextInputField
          label={t("Name")}
          text={name}
          onTextChange={setName}
          placeholder={t("Name eingeben ...")}
          fieldName={`${fieldNamePrefix}positions.${positionId}.${field}`}
        />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doAdd} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * RemoveGroupingDialog removes all positions of the grouping selected by
 * field, e.g. a building with all its rooms.
 */
function RemoveGroupingDialog({
  open,
  onOpenChange,
  grouping,
  field,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grouping: Grouping;
  field: "group" | "area";
}) {
  const { onGroupingRemove } = usePositionsTableContext();
  const run = useRunOperation();
  const [isLoading, setIsLoading] = useState(false);
  const label = useGroupingName(field);

  const doDelete = () => {
    if (isLoading) return;

    setIsLoading(true);
    void run(onGroupingRemove({ ...grouping })).then((ok) => {
      setIsLoading(false);
      if (ok) onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${label} ${t("löschen?")}`}</DialogTitle>
          <DialogDescription>
            {`${t("Alle Positionen in")} ${grouping[field]} ${t("werden unwiderruflich gelöscht.")}`}
          </DialogDescription>
        </DialogHeader>
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

// lockedColumns are always shown
const lockedColumns: TableColumn[] = [
  TableColumn.DnDGrip,
  TableColumn.Number,
  TableColumn.Active,
  TableColumn.Text,
  TableColumn.FinalPrice,
  TableColumn.Actions,
];

/**
 * ColumnSettingsDropdown lets users choose the columns shown, apart from
 * the locked ones.
 */
function ColumnSettingsDropdown() {
  const { document, selectedColumns, setColumns } = usePositionsTableContext();

  const configurable = tableColumnsList.filter(
    (column) =>
      !lockedColumns.includes(column) &&
      (column !== TableColumn.ServiceObject ||
        (document.serviceObjects?.length ?? 0) > 1),
  );

  const toggle = (column: TableColumn, shown: boolean) => {
    const selected = shown
      ? [...selectedColumns, column]
      : selectedColumns.filter((c) => c !== column);
    setColumns(tableColumnsList.filter((c) => selected.includes(c)));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-8 rounded-none"
          aria-label={t("Spalten")}
          title={t("Spalten")}
        >
          <Settings2 />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t("Spalten")}</DropdownMenuLabel>
        {configurable.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={selectedColumns.includes(column)}
            onCheckedChange={(shown) => toggle(column, shown)}
            // keep the menu open to toggle several columns
            onSelect={(e) => e.preventDefault()}
          >
            {column.title}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
